import { useEffect, useRef, useState } from "react";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

// Google invokes this global when the API key is rejected (bad referrer / billing off).
if (typeof window !== "undefined" && !window.__gmapsAuthHooked) {
  window.__gmapsAuthHooked = true;
  window.gm_authFailure = () => {
    window.__gmapsAuthFailed = true;
    window.dispatchEvent(new Event("gmaps-auth-failed"));
  };
}

let placesPromise;
const loadPlaces = () => {
  if (!placesPromise) {
    placesPromise = (async () => {
      setOptions({ key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY, v: "weekly" });
      return await importLibrary("places");
    })();
  }
  return placesPromise;
};

// Google Places Autocomplete bound to a plain input.
// Captures { name, address, place_id, lat, lng } on selection.
// If Google Maps is unavailable (unauthorised key / no billing), the field
// degrades gracefully into a normal text input that still saves.
export const PlacesAutocomplete = ({ value, onTextChange, onSelect, placeholder, testid }) => {
  const inputRef = useRef(null);
  const acRef = useRef(null);
  const [failed, setFailed] = useState(typeof window !== "undefined" && !!window.__gmapsAuthFailed);

  useEffect(() => {
    const onFail = () => setFailed(true);
    window.addEventListener("gmaps-auth-failed", onFail);
    let listener;
    let cancelled = false;
    loadPlaces().then((places) => {
      try {
        if (cancelled || !inputRef.current || acRef.current || !places?.Autocomplete) return;
        acRef.current = new places.Autocomplete(inputRef.current, {
          fields: ["name", "formatted_address", "place_id", "geometry"],
        });
        listener = acRef.current.addListener("place_changed", () => {
          const p = acRef.current.getPlace();
          if (!p) return;
          const loc = p.geometry?.location;
          onSelect?.({
            name: p.name || p.formatted_address || inputRef.current.value,
            address: p.formatted_address || "",
            place_id: p.place_id || "",
            lat: loc ? loc.lat() : undefined,
            lng: loc ? loc.lng() : undefined,
          });
        });
      } catch (e) {
        setFailed(true);
      }
    }).catch(() => setFailed(true));
    return () => {
      cancelled = true;
      window.removeEventListener("gmaps-auth-failed", onFail);
      if (window.google && listener) window.google.maps.event.removeListener(listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {failed && <style>{`.pac-container{display:none !important;}`}</style>}
      <input
        ref={inputRef}
        defaultValue={value || ""}
        onChange={(e) => onTextChange?.(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="luxury-input w-full"
        autoComplete="off"
      />
      {failed && (
        <span className="mt-1 block text-[10px] text-[#96836B]">Saisie manuelle (autocomplétion Google indisponible).</span>
      )}
    </>
  );
};

export default PlacesAutocomplete;
